import {
  Action,
  PayloadAction,
  createSelector,
  createSlice
} from '@reduxjs/toolkit'
import { initialOptionValues } from 'const/defaultValue'
import { userAccountInfo } from 'helpers/user'
import type { OriginalRootState } from 'redux/rootReducer'
import type {
  GetProxPaymentsResponse,
  PaymentInfo,
  PaymentType,
  UpdatePaymentInfo
} from 'typings/payment'
import type { ErrorDataResponse } from 'typings/serverError'

export interface PaymentAction extends Action {
  payload: PaymentInfo | UpdatePaymentInfo
}

interface PaymentDetailsSuccessPayLoad extends GetProxPaymentsResponse {
  isPaymentInvoice?: string
}

interface PaymentDetailsErrorPayLoad extends ErrorDataResponse {
  isPaymentInvoice?: string
}

/** Default/initial value for payment type radio in BRC payment flow. */
const DEFAULT_BRC_PAYMENT_TYPE: PaymentType = 'scheduleOneTimePayment'

export const initState: PaymentInfo = {
  brcPaymentType: DEFAULT_BRC_PAYMENT_TYPE,
  paymentDate: '',
  selectedPayment: { value: '', label: '' },
  selectedAmount: { value: '', label: '' },
  accounts: [],
  unappliedAmount: '',
  confirmationEmail: {
    emailId: '',
    isValidEmail: true
  },
  totalPaymentAmount: '',
  paymentDetail: {},
  paymentCancelledStatus: '',
  isPaymentRefresh: false,
  isOneTimePaymentSuccess: false,
  isResponseFail: false,
  errorMessage: '',
  paymentId: '',
  isPaymentDetailLoading: true
}

export const paymentSlice = createSlice({
  name: 'paymentSlice',
  initialState: initState,
  reducers: {
    /**
     * @deprecated Avoid using this action. Marked for eventual removal.
     */
    updatePaymentState: (
      state: PaymentInfo,
      action: PayloadAction<PaymentInfo>
    ) => {
      return {
        ...state,
        ...action.payload
      }
    },
    setConfirmationEmail: (
      state: PaymentInfo,
      action: PayloadAction<PaymentInfo>
    ) => {
      return {
        ...state,
        confirmationEmail: { ...state.confirmationEmail, ...action.payload }
      }
    },
    setPaymentDetails: (
      state: PaymentInfo,
      action: PayloadAction<
        PaymentDetailsSuccessPayLoad | PaymentDetailsErrorPayLoad
      >
    ) => {
      return {
        ...state,
        paymentDetail: action.payload,
        isPaymentDetailLoading: false
      }
    },
    /** This action is fired when user opens the Prox Payment page */
    proxPaymentStart: state => {
      // clear all error state
      state.isBankUnauthorized = false
      state.isScheduledDateInvalid = false
      state.isPaymentAlreadyScheduled = false
      state.isInvalidRoutingNumber = false
    },
    /** This action is fired when the user leaves the prox payment page */
    proxPaymentEnd: state => {
      if (!state.confirmationEmail) {
        state.confirmationEmail = { ...initState.confirmationEmail }
      }
      /*
       * When user selects ‘return to account summary’ on step 4 make a payment, it trigger reload and our cleanup function calls this to reset
       * any unsaved ‘confirmation email’ changes from our redux state. However, if the user decides to navigate back to steps 1-3 reload is not
       * called and the changes remains
       */
      state.confirmationEmail.emailId = ''
    },
    /**
     * Sets payment fields back to defaults.
     */
    resetPaymentOptions: (
      state,
      { payload }: PayloadAction<{ paymentType?: PaymentType }>
    ) => {
      state.paymentDate = ''
      state.unappliedAmount = ''
      state.selectedPayment = initialOptionValues
      state.selectedAmount = initialOptionValues
      state.isOneTimePaymentSuccess = false

      if (!userAccountInfo.isProx && payload.paymentType) {
        state.brcPaymentType = payload.paymentType
      }
    },
    /**
     * Updates the brcPaymentType field when user makes a selection.
     */
    brcPaymentTypeSelected: (
      state,
      { payload }: PayloadAction<PaymentType>
    ) => {
      state.brcPaymentType = payload
    }
  }
})

export const {
  brcPaymentTypeSelected,
  updatePaymentState,
  setConfirmationEmail,
  setPaymentDetails,
  proxPaymentStart,
  proxPaymentEnd,
  resetPaymentOptions
} = paymentSlice.actions

export default paymentSlice.reducer

/* SELECTORS */
const selectSelf = (state: OriginalRootState) => state.payment

/**
 * Selects the date, payment id, and amount for current payment form data.
 *
 * @returns {Object} payment Form field values
 * @returns {string} payment.paymentDate Payment date
 * @returns {string} payment.paymentId ID for the pay from account.
 * @returns {string} payment.paymentAmount Amount to pay.
 */
export const selectPaymentFormData = createSelector(
  selectSelf,
  ({ paymentDate, selectedPayment, selectedAmount }) => ({
    paymentDate,
    paymentId: selectedPayment.value,
    paymentAmount: selectedAmount.value
  })
)

/**
 * Returns the selected payment type for BRC payment accordion
 *
 * @returns {PaymentType}
 */
export const selectBrcPaymentType = createSelector(
  selectSelf,
  ({ brcPaymentType }) => brcPaymentType
)

/**
 * Returns api error message.
 *
 * @returns {string}
 */
export const selectPaymentErrorMessage = createSelector(
  selectSelf,
  ({ errorMessage }) => errorMessage
)
